import { useCallback, useEffect, useRef, useState } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { FileVideo, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { ImportVideo } from "@/components/app/import-video"
import {
  ProjectStorageBadge,
  useStorageUsage,
} from "@/components/app/project-storage-notice"
import type { ProjectStorage } from "@/components/app/project-storage-notice"
import { Card, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { videoClient } from "@/lib/video-client"
import { parseVideoSourceSearch } from "@/lib/video-source"

export const Route = createFileRoute("/app/")({
  validateSearch: parseVideoSourceSearch,
  component: WorkspacePage,
})

type VideoSummary = {
  id: string
  title: string
  status: "uploading" | "queued" | "processing" | "ready" | "failed"
  createdAt?: Date
  sourceThumbnailUrl?: string | null
  deletionPending?: boolean
  storage: ProjectStorage
}

function VideoThumbnail({ video }: { video: VideoSummary }) {
  const [imageFailed, setImageFailed] = useState(false)

  useEffect(() => setImageFailed(false), [video.sourceThumbnailUrl])

  if (!video.sourceThumbnailUrl || imageFailed) {
    return (
      <div className="app-video-thumbnail-fallback">
        <FileVideo aria-hidden="true" />
        <span>Thumbnail unavailable</span>
      </div>
    )
  }

  return (
    <img
      src={video.sourceThumbnailUrl}
      alt={`Thumbnail for ${video.title}`}
      className="app-video-thumbnail"
      referrerPolicy="no-referrer"
      onError={() => setImageFailed(true)}
    />
  )
}

function VideoCard({
  video,
  deleting,
  onDelete,
  onRename,
  onTrigger,
  plan,
}: {
  video: VideoSummary
  plan?: "free" | "basic" | "pro" | "high_volume"
  deleting: boolean
  onDelete: (video: VideoSummary) => void
  onRename: (video: VideoSummary) => void
  onTrigger: (trigger: HTMLButtonElement) => void
}) {
  const processingLabel = video.deletionPending
    ? null
    : video.status === "processing"
      ? "Processing video…"
      : video.status === "queued"
        ? "Waiting to process…"
        : video.status === "uploading"
          ? "Uploading video…"
          : null

  return (
    <Card className="app-video-card" size="sm">
      <Link
        to="/app/videos/$videoId"
        params={{ videoId: video.id }}
        className="app-video-card-media"
      >
        <VideoThumbnail video={video} />
        {processingLabel && (
          <div className="app-video-processing-overlay">
            <Badge variant="secondary" role="status">
              <Spinner aria-hidden="true" />
              {processingLabel}
            </Badge>
          </div>
        )}
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="secondary"
              size="icon"
              className="app-video-card-menu-trigger size-11"
            />
          }
          aria-label={`Actions for ${video.title}`}
          disabled={deleting}
          onClick={(event) => onTrigger(event.currentTarget)}
        >
          <MoreHorizontal aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuItem
              disabled={video.deletionPending}
              onClick={() => onRename(video)}
            >
              <Pencil aria-hidden="true" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => onDelete(video)}
            >
              <Trash2 aria-hidden="true" />
              {video.deletionPending ? "Retry deletion" : "Delete"}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <CardHeader>
        <ProjectStorageBadge storage={video.storage} plan={plan} />
        <CardTitle>
          <h3 className="app-video-card-title">
            <Link to="/app/videos/$videoId" params={{ videoId: video.id }}>
              {video.title}
            </Link>
          </h3>
        </CardTitle>
      </CardHeader>
    </Card>
  )
}

function WorkspacePage() {
  const { source } = Route.useSearch()
  const { storageUsage, refreshStorage } = useStorageUsage()
  const [videos, setVideos] = useState<VideoSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<VideoSummary | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [renameTarget, setRenameTarget] = useState<VideoSummary | null>(null)
  const [renameTitle, setRenameTitle] = useState("")
  const [renaming, setRenaming] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)
  const listRequest = useRef(0)
  const deletedIds = useRef(new Set<string>())
  const libraryRegion = useRef<HTMLElement>(null)
  const deleteTrigger = useRef<HTMLButtonElement | null>(null)

  const loadVideos = useCallback(async () => {
    const request = ++listRequest.current
    try {
      const next = await videoClient.videos.list()
      if (request !== listRequest.current) return
      setVideos(next.filter((video) => !deletedIds.current.has(video.id)))
      setError(null)
    } catch {
      if (request !== listRequest.current) return
      setError("We could not load your videos. Refresh the page to try again.")
    }
  }, [])

  useEffect(() => {
    void loadVideos()
    const timer = window.setInterval(() => void loadVideos(), 15_000)
    return () => window.clearInterval(timer)
  }, [loadVideos])

  async function renameVideo() {
    const title = renameTitle.trim()
    if (!renameTarget || !title || renaming) return
    setRenaming(true)
    setRenameError(null)
    try {
      const renamed = await videoClient.videos.rename({
        videoId: renameTarget.id,
        title,
      })
      listRequest.current += 1
      setVideos(
        (current) =>
          current?.map((video) =>
            video.id === renamed.id ? { ...video, title: renamed.title } : video
          ) ?? null
      )
      setRenameTarget(null)
      toast.success("Video renamed.")
    } catch {
      setRenameError("We could not rename this video. Try again.")
    } finally {
      setRenaming(false)
    }
  }

  async function deleteVideo() {
    if (!deleteTarget || deleting) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await videoClient.videos.delete({ videoId: deleteTarget.id })
      deletedIds.current.add(deleteTarget.id)
      setVideos(
        (current) =>
          current?.filter((video) => video.id !== deleteTarget.id) ?? null
      )
      setDeleteTarget(null)
      toast.success("Video and associated files deleted.")
      void loadVideos()
      void refreshStorage()
    } catch (failure) {
      const message =
        failure &&
        typeof failure === "object" &&
        "code" in failure &&
        failure.code === "CONFLICT" &&
        "message" in failure &&
        typeof failure.message === "string"
          ? failure.message
          : "Deletion could not be completed. Some files may already be removed. Try again to finish deleting this video."
      setDeleteError(message)
      void loadVideos()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <main className="app-main app-dashboard">
      <section aria-label="Import a video">
        <ImportVideo initialUrl={source} storageUsage={storageUsage} />
      </section>

      <Separator />

      <section
        className="app-recent"
        aria-label="Videos"
        ref={libraryRegion}
        tabIndex={-1}
      >
        {error && (
          <p className="app-data-error" role="status">
            {error}
          </p>
        )}
        {videos === null ? (
          <div className="app-video-list" aria-label="Loading recent videos">
            <Skeleton className="aspect-video w-full sm:aspect-auto sm:h-72" />
            <Skeleton className="aspect-video w-full sm:aspect-auto sm:h-72" />
          </div>
        ) : videos.length === 0 ? (
          <Empty className="app-empty-library">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Plus />
              </EmptyMedia>
              <EmptyTitle>Your first video starts above</EmptyTitle>
              <EmptyDescription>
                Once processing begins, it will appear here with its current
                status.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="app-video-list">
            {videos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                plan={storageUsage?.plan}
                deleting={deleting || renaming}
                onTrigger={(trigger) => {
                  deleteTrigger.current = trigger
                }}
                onDelete={(target) => {
                  setDeleteError(null)
                  setDeleteTarget(target)
                }}
                onRename={(target) => {
                  setRenameError(null)
                  setRenameTitle(target.title)
                  setRenameTarget(target)
                }}
              />
            ))}
          </div>
        )}
      </section>
      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open && !renaming) setRenameTarget(null)
        }}
      >
        <DialogContent
          showCloseButton={!renaming}
          finalFocus={() =>
            deleteTrigger.current?.isConnected
              ? deleteTrigger.current
              : libraryRegion.current
          }
        >
          <DialogHeader>
            <DialogTitle>Rename video</DialogTitle>
            <DialogDescription>
              Change the title shown in your video library.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void renameVideo()
            }}
            aria-busy={renaming}
          >
            <FieldGroup>
              <Field data-disabled={renaming} data-invalid={!!renameError}>
                <FieldLabel htmlFor="video-title">Title</FieldLabel>
                <Input
                  id="video-title"
                  required
                  maxLength={255}
                  disabled={renaming}
                  value={renameTitle}
                  aria-invalid={!!renameError}
                  aria-describedby={
                    renameError ? "video-title-error" : undefined
                  }
                  onChange={(event) => {
                    setRenameTitle(event.target.value)
                    setRenameError(null)
                  }}
                />
                {renameError && (
                  <FieldError id="video-title-error">{renameError}</FieldError>
                )}
              </Field>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={renaming}
                  onClick={() => setRenameTarget(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={renaming || !renameTitle.trim()}
                >
                  {renaming && <Spinner data-icon="inline-start" />}
                  {renaming ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent
          className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto"
          finalFocus={() =>
            deleteTrigger.current?.isConnected
              ? deleteTrigger.current
              : libraryRegion.current
          }
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this video permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes &quot;{deleteTarget?.title}&quot;, its source file,
              all shorts and exports, transcript, subtitle settings, and
              processing data from intoshort and its bucket. This cannot be
              undone. The original video on YouTube or Twitch is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleting}
              onClick={() => void deleteVideo()}
            >
              {deleting && <Spinner data-icon="inline-start" />}
              {deleting
                ? "Deleting…"
                : deleteError
                  ? "Try again"
                  : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
